/**
 * @author Artem Nakhodkin <artem.nakhodkin@racingpost.com>
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import rpMqBreakpoints, { breakpointCodes } from 'rp-mq-breakpoints';
import Svg from 'rp-react-svg-icon';
import normalizeHeadingDisplay from './utils/normalizeHeadingDisplay';

const { EXTRA_NARROW, NARROW, MEDIUM, WIDE, EXTRA_WIDE } = breakpointCodes;
/**
 * List of media queries
 */
const EXTRA_WIDE_MEDIA = rpMqBreakpoints.getBreakpointByCode(EXTRA_WIDE).media;
const WIDE_MEDIA = rpMqBreakpoints.getBreakpointByCode(WIDE).media;
const MEDIUM_MEDIA = rpMqBreakpoints.getBreakpointByCode(MEDIUM).media;
const NARROW_MEDIA = rpMqBreakpoints.getBreakpointByCode(NARROW).media;
const EXTRA_NARROW_MEDIA = rpMqBreakpoints.getBreakpointByCode(EXTRA_NARROW).media;

/**
 * Component that is used for rendering <NewsStory />
 * It corrects date format, when resizing window happens
 * It renders container correctly, when story has no image
 */
class NewsStory extends PureComponent {
    static propTypes = {
        story: PropTypes.shape({
            /**
             * News id
             */
            id: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]).isRequired,
            /**
             * News url
             */
            url: PropTypes.string,
            /**
             * News image
             */
            image: PropTypes.object,
            /**
             * News could be free or not
             */
            isFree: PropTypes.bool.isRequired,
            /**
             * News could be Scribble Live Blog or not
             */
            isLive: PropTypes.bool,
            /**
             * News LIVE label
             */
            liveLabel: PropTypes.string,
            /**
             * News sections
             */
            section: PropTypes.string,
            /**
             * News label
             */
            label: PropTypes.string,
            /**
             * News with video
             */
            videoIncluded: PropTypes.bool,
            /**
             * News with audio
             */
            audioIncluded: PropTypes.bool,
            /**
             * News headline
             */
            headline: PropTypes.string
        }).isRequired,
        className: PropTypes.string
    };

    static get defaultProps() {
        return {
            className: '',
        };
    }

    componentDidMount() {
        const { story } = this.props;

        if (!story.isLive) {
            const breakPointCode = rpMqBreakpoints.current();
            const { container, section, label, handleResize } = this;
            normalizeHeadingDisplay(container, section, label, breakPointCode);
            window.addEventListener('rpBreakpointsSwitch', handleResize);
        }
    }

    componentWillUnmount() {
        const { handleResize } = this;
        window.removeEventListener('rpBreakpointsSwitch', handleResize);
    }

    /**
     * Method, which is triggered, while screen is resizing
     */
    handleResize = (event) => {
        const breakPointCode = event.detail.breakpoint.code;
        const { container, section, label } = this;
        normalizeHeadingDisplay(container, section, label, breakPointCode);
    };

    /**
     * Method is used for rendering image
     */
    renderImage = () => {
        const { story } = this.props;

        return (
            story.image && story.image.url &&
            <div className="rh-newsStory__imageHolder sectionB__imageHolder">
                <picture className="rh-newsStory__picture">
                    <source
                        srcSet={story.image[`data-src-${EXTRA_WIDE}`]}
                        media={EXTRA_WIDE_MEDIA}
                    />
                    <source
                        srcSet={story.image[`data-src-${WIDE}`]}
                        media={WIDE_MEDIA}
                    />
                    <source
                        srcSet={story.image[`data-src-${MEDIUM}`]}
                        media={MEDIUM_MEDIA}
                    />
                    <source
                        srcSet={story.image[`data-src-${NARROW}`]}
                        media={NARROW_MEDIA}
                    />
                    <source
                        srcSet={story.image[`data-src-${EXTRA_NARROW}`]}
                        media={EXTRA_NARROW_MEDIA}
                    />
                    <img
                        className="rh-newsStory__image"
                        src={story.image.url}
                        alt={story.image.altText || ''}
                    />
                </picture>
            </div>
        );
    };

    /**
     * Method is used for rendering icon
     */
    renderIcon = () => {
        const { story } = this.props;
        const isImagePresent = story.image.url;
        const storyClassName = classNames(
            'rh-newsStory__icon',
            isImagePresent ? 'rh-newsStory__iconPremContent' : 'rh-newsStory__iconPremContent_noImg'
        );

        return (
            !story.isLive && story.image && !story.isFree &&
            <Svg
                isInlined
                size="Medium"
                iconName="subscribe"
                className={storyClassName}
            />
        );
    };

    /**
     * Method is used for rendering icon with video
     */
    renderVideoIcon = () => {
        const { story } = this.props;

        return (
            !story.isLive && story.videoIncluded &&
            <Svg
                size="Medium"
                iconName="videoHollow"
                className="rh-newsStory__icon rh-newsStory__iconVideo labelMedia__icon"
            />
        );
    };

    /**
     * Method is used for rendering icon with audio
     */
    renderAudioIcon = () => {
        const { story } = this.props;

        return (
            !story.isLive && story.audioIncluded &&
            <Svg
                size="Medium"
                iconName="audio"
                className="rh-newsStory__icon rh-newsStory__iconAudio labelMedia__icon"
            />
        );
    };

    /**
     * Method is used for rendering icon and label for live blog
     */
    renderLiveIcon = () => {
        const { story } = this.props;

        return (
            story.isLive &&
            <span className="story__live">
                <Svg
                    size="Large"
                    iconName="news-live-no-fill"
                    className="newsLiveIcon"
                />
            </span>
        );
    };

    /**
     * Method is used for rendering live label for live blog
     */
    renderLiveLabel = () => {
        const { story } = this.props;

        return (
            <span className="rh-newsStory__live storyLabel_live">
                {story.liveLabel ? story.liveLabel : 'LIVE'}&nbsp;
            </span>
        );
    };

    /**
     * Method for rendering Underlay
     */
    renderLiveUnderlay = () => {
        const { story } = this.props;

        return (
            story.isLive &&
            <div className="rh-newsStory_liveUnderlay" />
        );
    };

    /**
     * Method for rendering section
     */
    renderSection = () => {
        const { story } = this.props;

        return (
            story.section &&
            <span
                className="rh-newsStory__section storyLabel"
                ref={(node) => { this.section = node; }}
            >
                { story.isLive && this.renderLiveLabel() }
                {`${story.section} `}
            </span>
        );
    };

    /**
     * Method for rendering label
     */
    renderLabel = () => {
        const { story } = this.props;

        return (
            story.label &&
            <span className="rh-newsStory__label storyLabel" ref={(node) => { this.label = node; }}>
                {story.label}
            </span>
        );
    };

    render() {
        const { story } = this.props;
        const {
            renderImage,
            renderIcon,
            renderSection,
            renderLabel,
            renderVideoIcon,
            renderAudioIcon,
            renderLiveUnderlay,
            renderLiveIcon
        } = this;

        const isImagePresent = Boolean(story.image.url);
        const headlineHolderClass = classNames(
            'rh-newsStory__headlineHolder',
            'sectionB__headline', 'headline',
            { 'rh-newsStory__headlineHolder_noImg': !isImagePresent }
        );
        const storyClasses = classNames(
            'sectionB',
            'rh-newsStory', this.props.className,
            { 'rh-newsStory_live': story.isLive }
        );

        const renderHeadlineHolder = () => (
            <div className={headlineHolderClass}>
                <div
                    className="rh-newsStory__sectionWrapper"
                    ref={(node) => { this.container = node; }}
                >
                    { renderIcon() }
                    { renderLiveIcon() }
                    <span className={classNames({ 'rh-newsStory__topic': story.isLive })}>
                        { renderSection() }
                        { renderLabel() }
                    </span>
                </div>
                <h3 className="rh-newsStory__headline headline">
                    <span className="rh-newsStory__iconWrp">
                        { renderVideoIcon() }
                        { renderAudioIcon() }
                    </span>
                    <span className="rh-newsStory__headline-title">{story.headline}</span>
                </h3>
            </div>
        );

        return (
            <a href={story.url} className={storyClasses}>
                { renderImage() }
                { renderHeadlineHolder() }
                { renderLiveUnderlay() }
            </a>
        );
    }
}

/**
 * Module exports
 */
export { NewsStory as default };



// WEBPACK FOOTER //
// ./src/js/News/components/NewsStory/NewsStory.js